import { log } from "../../chatframe/Utils";

/**
 * MediaDeviceMgr class  handles media device settings
 * for video call
 * @class {MediaDeviceMgr}
 */
export default class MediaDeviceMgr {
    settingsView: any;

    videoElement: any;

    audioInputSelect: any;

    audioOutputSelect: any;

    videoSelect: any;

    selectors: any[];

    volumeContainer: any;

    useVideo: boolean;

    constructor(view: any) {
        this.settingsView = view;
        this.videoElement = null;
        this.audioInputSelect = null;
        this.audioOutputSelect = null;
        this.videoSelect = null;
        this.selectors = [];
    }

    showMediaDeviceSettings(videoElement: any, audioInput: any, audioOutput: any,
        videoOutput: any, volumeContainer: any): void {

        this.videoElement = videoElement;
        this.audioInputSelect = audioInput;
        this.audioOutputSelect = audioOutput;
        this.videoSelect = videoOutput;
        this.volumeContainer = volumeContainer;

        this.selectors = [this.audioInputSelect, this.audioOutputSelect];

        if (!("sinkId" in HTMLMediaElement.prototype)) {
            this.audioOutputSelect.style.display = "none";
            this.audioOutputSelect.parentElement.firstElementChild.style.display = "none";
        }

        this.audioInputSelect.onchange = this.start.bind(this);
        this.audioOutputSelect.onchange = this.changeAudioDestination.bind(this);

        if (this.videoSelect) {
            this.selectors.push(this.videoSelect);
            this.videoSelect.onchange = this.start.bind(this);
            this.useVideo = true;
        } else {
            this.useVideo = false;
        }

        this.start();
    }

    start(): void {
        const audioSource = this.audioInputSelect.value;
        const videoSource = this.useVideo ? this.videoSelect.value : "";
        const constraints = {
            audio: audioSource ? { deviceId: { exact: audioSource } } : true,
            video: videoSource ? { deviceId: { exact: videoSource } } : this.useVideo,
        };

        /*
         * The MediaDevices.getUserMedia() method prompts the user for permission to
         * use a media input which produces a MediaStream with tracks containing the
         * requested types of media
         */
        if (this.volumeContainer != null) {
            navigator.mediaDevices.getUserMedia(constraints)
                .then(this.gotStream.bind(this))
                .then(this.gotDevices.bind(this))
                .catch(this.handleError.bind(this));
        } else {
            navigator.mediaDevices.enumerateDevices()
                .then(this.gotDevices.bind(this))
                .catch(this.handleError.bind(this));
        }
    }

    /**
     * Calls back method where we deal with the
     * available media device information
     * @param {array} deviceInfos ala multimedia device informations
     */
    gotDevices(deviceInfos: any): void {
        // Handles being called several times to update labels. Preserve values.
        const values = this.selectors.map(select => select.value);
        this.removeMediaSelectorOldOptions();
        this.displayingAvailableMediaDevicesOptions(deviceInfos);
        this.updateMediaSelectorWithAvailableOptions(values);
        this.settingsView.setAudioVideoSource(this.audioInputSelect.value,
            this.useVideo ? this.videoSelect.value : null);
    }

    /**
     * Removes old media device options from media selector on change
     */
    removeMediaSelectorOldOptions(): void {
        this.selectors.forEach(select => {
            while (select.firstChild) {
                select.removeChild(select.firstChild);
            }
        });
    }

    /**
     * Updates media selectors with updated media device options
     * @param {array} values updated media devices
     */
    updateMediaSelectorWithAvailableOptions(values: any[]): void {
        this.selectors.forEach((select, selectorIndex) => {
            if (Array.prototype.slice.call(select.childNodes)
                .some((n: { value: any; }) => n.value === values[selectorIndex])) {
                select.value = values[selectorIndex];
            }
        });
    }

    /**
     * Displays available media devices eg: microphone,speaker,camera etc.
     * in select menu options by passing media device informations
     * @param {array} deviceInfos ifo of all media device
     */
    displayingAvailableMediaDevicesOptions(deviceInfos: any): void {
        for (let i = 0; i < deviceInfos.length; ++i) {
            const deviceInfo = deviceInfos[i];
            const option = document.createElement("option");
            option.value = deviceInfo.deviceId;

            if (deviceInfo.deviceId == "default") {
                option.selected = true;
            }

            if (deviceInfo.kind === "audioinput") {
                option.text = deviceInfo.label || `microphone ${this.audioInputSelect.length + 1}`;
                this.audioInputSelect.appendChild(option);
            } else if (deviceInfo.kind === "audiooutput") {
                option.text = deviceInfo.label || `speaker ${this.audioOutputSelect.length + 1}`;
                this.audioOutputSelect.appendChild(option);
            } else if (deviceInfo.kind === "videoinput" && this.videoSelect) {
                option.text = deviceInfo.label || `camera ${this.videoSelect.length + 1}`;
                this.videoSelect.appendChild(option);
            } else {
                log(`Some other kind of source/device: ${deviceInfo}`);
            }
        }
    }

    // Attach audio output device to video element using device/sink ID.
    attachSinkId(element: any, sinkId: any): void {
        if (typeof element.sinkId !== "undefined") {
            element.setSinkId(sinkId)
                .then(() => {
                    log(`Success, audio output device attached: ${sinkId}`);
                })
                .catch((error: any) => {
                    let errorMessage = error;

                    if (error.name === "SecurityError") {
                        errorMessage = `You need to use HTTPS for selecting 
                        audio output device: ${error}`;
                    }

                    console.error(errorMessage);

                    // Jump back to first output device in the list as it's the default.
                    this.audioOutputSelect.selectedIndex = 0;
                });
        } else {
            console.warn("Browser does not support output device selection.");
        }
    }

    changeAudioDestination(): void {
        const audioDestination = this.audioOutputSelect.value;
        this.settingsView.setSinkID(audioDestination);
    }

    /**
     * Success call back method for navigator.mediaDevices.getUserMedia
     * @param {MediaStream} stream  media stream value
     * @returns {enumerateDevices} enumerate media devices
     */
    gotStream(stream: any): Promise<MediaDeviceInfo[]> {
        // make stream available to global object\
        if (this.videoElement) {
            this.videoElement.srcObject = stream;
        }

        this.createAudioAnalyserForAvailableStream(stream);
        return navigator.mediaDevices.enumerateDevices();
    }

    /**
     * Creates input audio volume analyser for available stream object
     * @param {MediaStream} mediaStream media stream value
     */
    createAudioAnalyserForAvailableStream(mediaStream: any): void {
        if (this.volumeContainer) {
            if (typeof AudioContext !== "undefined") {
                const audioContext = new AudioContext();
                const analyser = audioContext.createAnalyser();
                const microphone = audioContext.createMediaStreamSource(mediaStream);
                const javascriptNode = audioContext.createScriptProcessor(2048, 1, 1);
                analyser.smoothingTimeConstant = 0.8;
                analyser.fftSize = 1024;
                microphone.connect(analyser);
                analyser.connect(javascriptNode);
                javascriptNode.connect(audioContext.destination);
                javascriptNode.onaudioprocess = function () {
                    const array = new Uint8Array(analyser.frequencyBinCount);
                    analyser.getByteFrequencyData(array);
                    let values = 0;
                    const length = array.length;

                    for (let i = 0; i < length; i++) {
                        values += (array[i]);
                    }

                    const average = values / length;
                    this.displayInputDeviceVolumeLevel(average);
                }.bind(this);
            } else {
                this.volumeContainer.style.display = "none";
            }
        }
    }

    displayInputDeviceVolumeLevel(vol: number): void {
        const all_pids = this.volumeContainer.getElementsByClassName("microphone-volume-level");

        if (all_pids) {
            const amout_of_pids = Math.round(vol / 10);

            for (let i = 0; i < all_pids.length; i++) {
                all_pids[i].style.backgroundColor = "#007bff3b";
            }

            for (let i = 0; i < amout_of_pids; i++) {
                if (all_pids[i]) {
                    all_pids[i].style.backgroundColor = "#007bff";
                }
            }
        }
    }

    handleError(error: any): void {
        log(`navigator.MediaDevices.getUserMedia error: ${error.message}, ${error.name}`);
    }

    removeStream(): void {
        if (this.videoElement && this.videoElement.srcObject) {
            this.videoElement.srcObject.getTracks().forEach((track: any) => {
                track.stop();
            });
            this.videoElement.srcObject = null;
        }
    }

    clear(): void {
        if (this.audioInputSelect) {
            this.audioInputSelect.onchange = null;
            this.audioOutputSelect.onchange = null;

            if (this.videoSelect) {
                this.videoSelect.onchange = null;
            }
        }

        this.removeStream();
    }
}
